import { FacilityDropdownInput } from "#src/batteries-included-components/Dropdowns";
import { AssetBuilderFiltersType } from "#src/batteries-included-components/FilterAreas/AssetBuilderFilterAreas/AssetBuilderFilterAreas";
import { AssetTemplateRadioInput } from "#src/batteries-included-components/Forms/Fields/AssetTemplateRadioInput";
import { useListEquipmentTypes } from "#src/components/hooks/adapters/useEquipment";
import { useListTemplatedConfigurations } from "#src/components/hooks/adapters/useTemplatedConfigurations";
import useLocalization from "#src/hooks/useLocalization";
import { useMultiStepFormContext } from "#src/hooks/useMultiStepForm";
import { useStickyState } from "#src/hooks/useStickyState";
import {
  DropdownInput,
  Form,
  Panel,
  type StorageKeys,
  TextInput,
  useForm,
} from "@validereinc/common-components";
import {
  AssetType,
  AssetTypeType,
  EquipmentSchema,
  Resources,
  TemplatedConfigurationCompanyStatus,
  TemplatedConfigurationSchema,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useEffect } from "react";
import styles from "./EquipmentFormPanel.module.scss";
const cx = classNames.bind(styles);

const EquipmentOverviewFormStep = ({
  filterConfigStorageKey,
  shouldUseAssetBuilder,
}: {
  shouldUseAssetBuilder?: boolean;
} & Pick<StorageKeys, "filterConfigStorageKey">) => {
  const urlSearchParams = new URLSearchParams(document.location.search);
  const facilityId = urlSearchParams.get("facilityId");

  const { currentStep, updateStep } = useMultiStepFormContext();
  const STEP_NUMBER = 1;

  const [filters] = useStickyState<AssetBuilderFiltersType>(
    {} as AssetBuilderFiltersType,
    filterConfigStorageKey
  );
  const { displayName, sortDirection } = filters;

  const { data: equipmentTypesData, isLoading: isLoadingEquipmentTypes } =
    useListEquipmentTypes({});

  const { localize, isLoading: isMappingLoading } = useLocalization();

  const form = useForm<{
    asset_type: AssetTypeType;
    name?: string;
    type_id: string;
    template_name?: string;
    facility_id?: string;
  }>({
    defaultValues: {
      asset_type: AssetType.EQUIPMENT,
      name: "",
      type_id: "",
      template_name: "",
      facility_id: facilityId ?? "",
    },
  });

  const formValues = form.watch();

  const isTemplateQueryEnabled = shouldUseAssetBuilder;
  const availableTemplatesQuery = useListTemplatedConfigurations(
    {
      filters: {
        primary_resource_type: Resources.EQUIPMENT,
        [`entity_subtypes.${Resources.EQUIPMENT}`]: formValues.type_id,
        display_name: displayName,
        // @ts-expect-error TODO: Typing on filter
        templated_configuration_company_status: {
          $exact: TemplatedConfigurationCompanyStatus.ACTIVE,
        },
      },
      sortDirection,
      sortBy: TemplatedConfigurationSchema.keyof().Enum.display_name,
    },
    { enabled: isTemplateQueryEnabled }
  );

  const availableTemplatesData = availableTemplatesQuery.data?.data ?? [];

  const availableTemplatesOptions = availableTemplatesData.map((template) => {
    return {
      title: template.display_name,
      description: template.description ?? "",
      value: template.name,
    };
  });

  const handleTypeIdChange = () => {
    form.setValue("template_name", "");
  };

  const handleTemplateNameChange = (templateName: string) => {
    const equipmentTypeIds =
      availableTemplatesData?.find((template) => template.name === templateName)
        ?.entity_subtypes[Resources.EQUIPMENT] ?? [];
    if (equipmentTypeIds?.includes(formValues.type_id)) {
      return;
    }

    const newTypeId =
      availableTemplatesData?.find((template) => template.name === templateName)
        ?.entity_subtypes[Resources.EQUIPMENT]?.[0] ?? "";

    form.setValue(EquipmentSchema.keyof().Enum.type_id, newTypeId, {
      shouldValidate: true,
    });
  };

  useEffect(() => {
    updateStep(STEP_NUMBER, {
      getValues: () => form.getValues(),
      getFormState: () => ({
        isSubmitting: false,
        isValid:
          !!formValues.type_id &&
          (!shouldUseAssetBuilder
            ? !!formValues.facility_id && !!formValues.name
            : !!formValues.template_name),
      }),
    });
  }, [JSON.stringify(formValues)]);

  const isLoading = isMappingLoading || isLoadingEquipmentTypes;
  if (currentStep !== STEP_NUMBER) return null;

  return (
    <Panel
      className={cx("panel")}
      loaded={!isLoading}
    >
      <Form {...form}>
        <h4 className={cx("section-title")}>Overview</h4>
        {shouldUseAssetBuilder ? null : (
          <p
            className={cx("section-description")}
          >{`Quick Create will create only the single ${localize(AssetType.EQUIPMENT)} whose details are specified below.`}</p>
        )}
        <div className={cx("basic-info-container")}>
          {shouldUseAssetBuilder ? (
            <DropdownInput
              label="Asset Type"
              name="asset_type"
              options={[
                {
                  label: localize(AssetType.EQUIPMENT),
                  value: AssetType.EQUIPMENT,
                },
              ]}
              labelKey="label"
              valueKey="value"
              isRequired
              isDisabled
              isFluid
            />
          ) : (
            <TextInput
              label="Name"
              name={EquipmentSchema.keyof().Enum.name}
              placeholder="Enter Name..."
              isFluid
              isRequired
            />
          )}

          <DropdownInput
            label={`${localize(Resources.EQUIPMENT)} Type`}
            name={EquipmentSchema.keyof().Enum.type_id}
            options={equipmentTypesData?.data ?? []}
            placeholder={`Select ${localize(Resources.EQUIPMENT)} Type...`}
            isSortedAlphabetically
            valueKey="id"
            labelKey="name"
            onChange={handleTypeIdChange}
            isRequired
            isFluid
            isLoading={isLoadingEquipmentTypes}
          />

          {shouldUseAssetBuilder ? (
            <>
              <AssetTemplateRadioInput
                filterConfigStorageKey={filterConfigStorageKey}
                assetType={AssetType.EQUIPMENT}
                isTemplateQueryDisabled={!isTemplateQueryEnabled}
                availableTemplatesQueryIsLoading={
                  availableTemplatesQuery.isLoading
                }
                availableTemplatesOptions={availableTemplatesOptions}
                handleOnChange={(templateName: string) =>
                  handleTemplateNameChange(templateName)
                }
                isOptionSelected={Boolean(formValues.template_name)}
              />
            </>
          ) : (
            <FacilityDropdownInput
              name="facility_id"
              placeholder={`Select ${localize(`${AssetType.FACILITY}`)}...`}
              isFluid
              isDisabled={Boolean(facilityId)}
              isMulti={false}
              isOptionalTextShown={false}
              isRequired
            />
          )}
        </div>
      </Form>
    </Panel>
  );
};

export default EquipmentOverviewFormStep;
